import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PRIMARY_BTN_CLS, SECONDARY_BTN_CLS } from "@/components/premium";

// =====================================================================
// InviteBusinessDialog — shared by AdminBusinesses ("Inviter bedrift")
// and Forespørsler ("konverter forespørsel → bedrift").
// =====================================================================
//
// Two modes:
//   1. Regular invite (default):
//        POST /api/admin/invitations
//        defaultPlan = "basic"
//
//   2. Convert-from-lead (pass `leadId` + `initial` prefill):
//        POST /api/contact/requests/{leadId}/convert
//        defaultPlan = "premium"  (caller's choice; spec: Premium)
//
// Both modes return the same shape from the API: business_id, user_id,
// slug, invite_status, email_sent. The convert endpoint adds lead_id
// and lead_status so the Forespørsler list can flip the badge to
// "Konvertert" without a roundtrip.
//
// `onCreated(response)` fires on success — the response includes the
// full API payload, NOT just a void.
//
// The dialog's data-testid attributes match the legacy AdminBusinesses
// dialog so existing tests keep working (invite-bedrift-dialog,
// invite-business-name, invite-owner-name, invite-owner-email,
// invite-phone, invite-plan, invite-access, invite-billing,
// invite-pilot-end, invite-price, invite-note, invite-submit).
// =====================================================================

const EMPTY = {
  business_name: "",
  owner_name: "",
  owner_email: "",
  phone: "",
  plan: "basic",
  access_status: "active",
  billing_status: "not_decided",
  pilot_end_date: "",
  agreed_monthly_price_nok: "",
  internal_note: "",
};

function buildInitial(initial, defaultPlan) {
  return {
    ...EMPTY,
    plan: defaultPlan || "basic",
    ...(initial || {}),
  };
}

export function InviteBusinessDialog({
  open,
  onOpenChange,
  onCreated,
  initial = null,
  leadId = null,
  defaultPlan = "basic",
}) {
  const [form, setForm] = useState(() => buildInitial(initial, defaultPlan));
  const [submitting, setSubmitting] = useState(false);

  // Re-seed every time the dialog opens so reopening it on a different
  // lead picks up the new prefill — without this, the second click on
  // "Inviter bedrift" for a different lead would still show the first
  // lead's data because useState only runs the initialiser once.
  useEffect(() => {
    if (open) setForm(buildInitial(initial, defaultPlan));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.business_name.trim() || !form.owner_name.trim() || !form.owner_email.trim()) {
      toast.error("Bedriftsnavn, eier-navn og e-post er påkrevd.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        business_name: form.business_name.trim(),
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        plan: form.plan,
        access_status: form.access_status,
        billing_status: form.billing_status,
        pilot_end_date: form.pilot_end_date || null,
        agreed_monthly_price_nok: form.agreed_monthly_price_nok === ""
          ? null : Number(form.agreed_monthly_price_nok),
        internal_note: form.internal_note.trim() || null,
      };
      const url = leadId
        ? `/contact/requests/${leadId}/convert`
        : "/admin/invitations";
      const r = await api.post(url, payload);
      if (r.data?.email_sent === false) {
        toast.warning("Invitasjonen ble opprettet, men e-posten kunne ikke sendes.", {
          description: "Sjekk e-postoppsettet (Resend-API-nøkkel og avsenderdomene) og bruk «Send ny invitasjon» fra Administrer-dialogen når problemet er løst.",
          duration: 10000,
        });
      } else if (leadId) {
        toast.success("Forespørsel konvertert — invitasjon sendt på e-post.");
      } else {
        toast.success("Invitasjon sendt på e-post.");
      }
      onCreated?.(r.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Kunne ikke opprette invitasjon");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="invite-bedrift-dialog">
        <DialogHeader>
          <DialogTitle>{leadId ? "Konverter forespørsel til bedrift" : "Inviter ny bedrift"}</DialogTitle>
          <DialogDescription>
            {leadId
              ? "Feltene er forhåndsutfylt fra forespørselen. Juster pakke og pilotperiode før du sender. Forespørselen markeres som «Konvertert» automatisk når invitasjonen er sendt."
              : "Lederen mottar en e-post med aktiveringslenke (gyldig i 48 timer) og setter sitt eget passord. Ingen midlertidige passord opprettes."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label htmlFor="biz-name">Bedriftsnavn *</Label>
            <Input
              id="biz-name" required value={form.business_name}
              onChange={(e) => set("business_name", e.target.value)}
              data-testid="invite-business-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="owner-name">Leder (navn) *</Label>
              <Input
                id="owner-name" required value={form.owner_name}
                onChange={(e) => set("owner_name", e.target.value)}
                data-testid="invite-owner-name"
              />
            </div>
            <div>
              <Label htmlFor="owner-email">Leder e-post *</Label>
              <Input
                id="owner-email" required type="email" value={form.owner_email}
                onChange={(e) => set("owner_email", e.target.value)}
                data-testid="invite-owner-email"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="biz-phone">Telefon</Label>
              <Input
                id="biz-phone" value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                data-testid="invite-phone"
              />
            </div>
            <div>
              <Label>Pakke</Label>
              <Select value={form.plan} onValueChange={(v) => set("plan", v)}>
                <SelectTrigger data-testid="invite-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tilgangsstatus</Label>
              <Select value={form.access_status} onValueChange={(v) => set("access_status", v)}>
                <SelectTrigger data-testid="invite-access"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktiv</SelectItem>
                  <SelectItem value="disabled">Deaktivert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fakturering</Label>
              <Select value={form.billing_status} onValueChange={(v) => set("billing_status", v)}>
                <SelectTrigger data-testid="invite-billing"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free_pilot">Gratis pilot</SelectItem>
                  <SelectItem value="manual">Manuell faktura</SelectItem>
                  <SelectItem value="not_decided">Ikke avgjort</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="pilot-end">Pilot t.o.m. (valgfri)</Label>
              <Input
                id="pilot-end" type="date" value={form.pilot_end_date}
                onChange={(e) => set("pilot_end_date", e.target.value)}
                data-testid="invite-pilot-end"
              />
            </div>
            <div>
              <Label htmlFor="agreed-price">Avtalt pris (kr/mnd, valgfri)</Label>
              <Input
                id="agreed-price" type="number" min={0} value={form.agreed_monthly_price_nok}
                onChange={(e) => set("agreed_monthly_price_nok", e.target.value)}
                data-testid="invite-price"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="internal-note">Internt notat (kun for ClientFlow-admin)</Label>
            <Textarea
              id="internal-note" rows={2} maxLength={2000} value={form.internal_note}
              onChange={(e) => set("internal_note", e.target.value)}
              data-testid="invite-note"
              placeholder="F.eks. 'Pilot til 01.07 — vurderer Pro etterpå'"
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button" variant="outline" className={SECONDARY_BTN_CLS}
              onClick={() => onOpenChange(false)} disabled={submitting}
            >
              Avbryt
            </Button>
            <Button
              type="submit" className={PRIMARY_BTN_CLS} disabled={submitting}
              data-testid="invite-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              <Mail className="w-4 h-4" strokeWidth={1.75} />
              {leadId ? "Konverter & send invitasjon" : "Send invitasjon"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default InviteBusinessDialog;
